const ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;
const UINT_RE = /^(0|[1-9]\d*)$/;

const normalizeAddress = (value) => {
  const address = typeof value === "string" ? value.trim() : "";
  return ADDRESS_RE.test(address) ? address.toLowerCase() : null;
};

export const isAssignedV2ProjectCertifier = (project, accountAddress) => {
  const protocol = project?.data?.v2_protocol;
  if (protocol?.protocolVersion !== "V2") return false;
  const certifier = normalizeAddress(protocol.certifier);
  const account = normalizeAddress(accountAddress);
  return Boolean(certifier && account && certifier === account);
};

export const isV2CertifierAssignmentAccepted = (readiness) =>
  readiness?.indexedState?.project?.certifierAssignmentAccepted === true;

export const isV2AuditActionAlreadyAppliedConflict = (error, actionKey) =>
  actionKey === "certifierAssignmentAccept" &&
  Number(error?.response?.status) === 409 &&
  error?.response?.data?.detail ===
    "certifier assignment already accepted";

export const resolveV2StageAwaitingPrincipalRelease = (readiness) => {
  const stages = readiness?.indexedState?.stages;
  if (!stages || typeof stages !== "object" || Array.isArray(stages)) {
    return null;
  }

  const candidates = [];
  for (const row of Object.values(stages)) {
    if (!row || typeof row !== "object") return null;
    const year = row.year;
    const stage = row.stage;
    const issuedUnits = String(row.issuedUnits ?? "");
    if (
      !Number.isInteger(year) ||
      year < 1 ||
      year > 20 ||
      !Number.isInteger(stage) ||
      stage < 0 ||
      stage > 14 ||
      !UINT_RE.test(issuedUnits) ||
      typeof row.settlementState !== "string"
    ) {
      return null;
    }
    if (
      BigInt(issuedUnits) > 0n &&
      row.settlementState === "funding"
    ) {
      candidates.push({ year, stage, issuedUnits });
    }
  }

  candidates.sort(
    (left, right) => left.year - right.year || left.stage - right.stage
  );
  return candidates[0] || null;
};

export const resolveV2CreditIssuanceCheckpoint = (readiness) => {
  const lifecycle = readiness?.lifecycle;
  if (
    !lifecycle ||
    !Array.isArray(lifecycle.stages) ||
    !Array.isArray(lifecycle.years)
  ) {
    return null;
  }

  const stagesByYear = new Map();
  for (const row of lifecycle.stages) {
    if (
      !row ||
      !Number.isInteger(row.year) ||
      row.year < 1 ||
      row.year > 20 ||
      !Number.isInteger(row.stage) ||
      row.stage < 0 ||
      row.stage > 14 ||
      typeof row.settlementState !== "string"
    ) {
      return null;
    }
    const rows = stagesByYear.get(row.year) || [];
    rows.push(row);
    stagesByYear.set(row.year, rows);
  }

  const years = [...lifecycle.years].sort(
    (left, right) => Number(left?.year) - Number(right?.year)
  );
  for (const yearState of years) {
    if (
      !yearState ||
      !Number.isInteger(yearState.year) ||
      yearState.year < 1 ||
      yearState.year > 20 ||
      typeof yearState.finalized !== "boolean"
    ) {
      return null;
    }
    if (yearState.finalized) continue;
    const stages = stagesByYear.get(yearState.year) || [];
    if (
      stages.length > 0 &&
      stages.every((row) => row.settlementState === "developerPaid")
    ) {
      return yearState;
    }
  }
  return null;
};

export const resolveV2PredictedWholeCredits = (project, year) => {
  if (!Number.isInteger(year) || year < 1 || year > 20) return null;
  const protocol = project?.data?.v2_protocol;
  const years = protocol?.protocolSchedule?.years;
  if (protocol?.protocolVersion !== "V2" || !Array.isArray(years)) {
    return null;
  }
  const matches = years.filter((row) => row?.protocolYear === year);
  if (matches.length !== 1) return null;
  const predictedCreditsWad = matches[0].predictedCreditsWad;
  if (
    typeof predictedCreditsWad !== "string" ||
    !UINT_RE.test(predictedCreditsWad)
  ) {
    return null;
  }
  const whole = BigInt(predictedCreditsWad) / 10n ** 18n;
  return whole > 0n ? whole.toString() : null;
};
